<template>
  <div
    @click="$emit('click', notification)"
    class="p-4 cursor-pointer transition-colors relative"
    :class="[
      notification.read
        ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
        : 'bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30'
    ]"
  >
    <!-- Unread indicator -->
    <div
      v-if="!notification.read"
      class="absolute top-4 right-4 w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full"
    ></div>

    <div class="flex items-start space-x-3">
      <!-- Icon -->
      <div
        class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
        :class="iconClasses"
      >
        <component :is="notificationIcon" class="w-5 h-5" />
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0 pr-6">
        <p class="text-sm font-medium text-gray-900 dark:text-white mb-1">
          {{ notification.title }}
        </p>
        <p class="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
          {{ notification.message }}
        </p>
        <p class="text-xs text-gray-400 dark:text-gray-500">
          {{ formatTime(notification.timestamp) }}
        </p>
      </div>

      <!-- Actions -->
      <div class="flex-shrink-0 flex items-center space-x-1">
        <button
          v-if="!notification.read"
          @click.stop="$emit('markRead', notification.id)"
          class="p-1.5 text-gray-400 hover:text-primary-600 dark:text-gray-500 dark:hover:text-primary-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          :title="$t('common.notifications.mark_as_read')"
        >
          <CheckIcon class="w-4 h-4" />
        </button>

        <button
          @click.stop="$emit('remove', notification.id)"
          class="p-1.5 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          :title="$t('common.notifications.remove')"
        >
          <XMarkIcon class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  CheckIcon,
  XMarkIcon,
  EnvelopeIcon,
  BellIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  RocketLaunchIcon,
  PaperAirplaneIcon
} from '@heroicons/vue/24/outline'

const { t } = useI18n()

const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
})

defineEmits(['click', 'markRead', 'remove'])

const notificationIcon = computed(() => {
  const iconMap = {
    email: EnvelopeIcon,
    campaign: RocketLaunchIcon,
    campaign_started: RocketLaunchIcon,
    campaign_completed: CheckCircleIcon,
    campaign_failed: ExclamationTriangleIcon,
    campaign_progress: PaperAirplaneIcon,
    warning: ExclamationTriangleIcon,
    success: CheckCircleIcon,
    info: InformationCircleIcon,
    error: ExclamationTriangleIcon,
    default: BellIcon
  }

  return iconMap[props.notification.type] || iconMap.default
})

const iconClasses = computed(() => {
  const classMap = {
    email: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
    campaign: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400',
    campaign_started: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400',
    campaign_completed: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
    campaign_failed: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
    campaign_progress: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
    warning: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-400',
    success: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
    info: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
    error: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
    default: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
  }

  return classMap[props.notification.type] || classMap.default
})

const formatTime = (timestamp) => {
  const now = new Date()
  const time = new Date(timestamp)
  const diffInSeconds = Math.floor((now - time) / 1000)

  if (diffInSeconds < 60) {
    return t('common.notifications.time.just_now')
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return t('common.notifications.time.minutes_ago', { count: minutes })
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return t('common.notifications.time.hours_ago', { count: hours })
  } else {
    const days = Math.floor(diffInSeconds / 86400)
    return t('common.notifications.time.days_ago', { count: days })
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
